import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Award, Gift, Plus, Edit, Trash2, Loader2, Save, X, CheckCircle2, Clock } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { RankBadge } from './RankBadge';
import {
  adminGetMilestones, adminCreateMilestone, adminUpdateMilestone, adminDeleteMilestone,
  adminGetClaims, adminFulfillClaim, getPaymentTiers
} from '../services/api';

const ICON_OPTIONS = ['cadet', 'officer', 'sergeant', 'goldshield', 'lieutenant', 'captain', 'commander', 'chief'];
const COLOR_OPTIONS = ['gray', 'amber', 'slate', 'yellow', 'red'];

const emptyForm = { months: 0, rankName: '', isOG: false, icon: 'award', color: 'amber', rewardDescription: '', enabled: true, tierRewards: {} };

const GamificationAdminPanel = () => {
  const [milestones, setMilestones] = useState([]);
  const [claims, setClaims] = useState([]);
  const [paidTiers, setPaidTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [ms, cl, tiersResp] = await Promise.all([adminGetMilestones(), adminGetClaims(), getPaymentTiers()]);
      setMilestones(ms);
      setClaims(cl);
      const tiersObj = tiersResp.tiers || tiersResp || {};
      const paid = Object.entries(tiersObj)
        .map(([id, t]) => ({ id, name: t.name, level: t.tier_level }))
        .filter(t => t.level > 0)
        .sort((a, b) => a.level - b.level);
      setPaidTiers(paid);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load gamification data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (m) => {
    setEditing(m);
    // Build per-tier form rewards with an editable optionsText field
    const tr = {};
    Object.entries(m.tierRewards || {}).forEach(([lvl, r]) => {
      tr[lvl] = {
        title: r.title || '',
        description: r.description || '',
        optionsText: (r.options || []).join('\n'),
      };
    });
    setForm({
      months: m.months, rankName: m.rankName, isOG: m.isOG, icon: m.icon || 'award',
      color: m.color || 'amber', rewardDescription: m.rewardDescription || '',
      enabled: m.enabled !== false, tierRewards: tr,
    });
    setDialogOpen(true);
  };

  const setTierReward = (level, field, value) => {
    setForm(prev => ({
      ...prev,
      tierRewards: {
        ...prev.tierRewards,
        [level]: { title: '', description: '', optionsText: '', ...prev.tierRewards[level], [field]: value },
      },
    }));
  };

  const handleSave = async () => {
    if (!form.rankName.trim()) {
      toast({ title: 'Rank name required', variant: 'destructive' });
      return;
    }
    // Convert form tierRewards -> API shape (keep only tiers with a title)
    const tierRewards = {};
    Object.entries(form.tierRewards || {}).forEach(([lvl, r]) => {
      if ((r.title || '').trim()) {
        tierRewards[lvl] = {
          title: r.title.trim(),
          description: (r.description || '').trim(),
          options: (r.optionsText || '').split('\n').map(o => o.trim()).filter(Boolean),
        };
      }
    });
    const payload = {
      months: form.months, rankName: form.rankName, isOG: form.isOG, icon: form.icon,
      color: form.color, rewardDescription: form.rewardDescription, enabled: form.enabled,
      tierRewards,
    };
    try {
      setSaving(true);
      if (editing) {
        await adminUpdateMilestone(editing.id, payload);
        toast({ title: 'Milestone updated', description: `${form.rankName} saved` });
      } else {
        await adminCreateMilestone(payload);
        toast({ title: 'Milestone created', description: `${form.rankName} added` });
      }
      setDialogOpen(false);
      await load();
    } catch (error) {
      toast({ title: 'Save failed', description: error.response?.data?.detail || 'Error', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (m) => {
    if (!window.confirm(`Delete the "${m.rankName}" milestone? This cannot be undone.`)) return;
    try {
      await adminDeleteMilestone(m.id);
      toast({ title: 'Deleted', description: `${m.rankName} removed` });
      await load();
    } catch (error) {
      toast({ title: 'Delete failed', description: error.response?.data?.detail || 'Error', variant: 'destructive' });
    }
  };

  const handleFulfill = async (claim) => {
    try {
      await adminFulfillClaim(claim.id);
      toast({ title: 'Marked fulfilled', description: `${claim.userDisplayName}'s reward` });
      await load();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Error', variant: 'destructive' });
    }
  };

  const pendingCount = claims.filter(c => c.status === 'pending').length;

  if (loading) {
    return <div className="text-center py-12"><Loader2 className="w-8 h-8 text-amber-500 animate-spin mx-auto" /></div>;
  }

  return (
    <Card className="bg-gray-900/90 border-2 border-amber-900/30" data-testid="gamification-admin-panel">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="font-headline text-xl text-amber-500 tracking-wider flex items-center gap-2">
              <Award className="w-5 h-5" /> RANKS &amp; REWARDS
            </CardTitle>
            <CardDescription className="font-typewriter text-gray-400">
              Edit the tenure ladder and fulfill reward claims
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="claims" className="space-y-4">
          <TabsList className="bg-gray-950 border border-amber-900/30">
            <TabsTrigger value="claims" className="font-headline tracking-wide data-[state=active]:bg-amber-900/50 data-[state=active]:text-amber-500" data-testid="admin-claims-tab">
              <Gift className="w-4 h-4 mr-2" /> Reward Claims
              {pendingCount > 0 && <Badge className="ml-2 bg-red-600 text-white">{pendingCount}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="ladder" className="font-headline tracking-wide data-[state=active]:bg-amber-900/50 data-[state=active]:text-amber-500" data-testid="admin-ladder-tab">
              <Award className="w-4 h-4 mr-2" /> The Ladder
            </TabsTrigger>
          </TabsList>

          {/* Claims inbox */}
          <TabsContent value="claims">
            {claims.length === 0 ? (
              <div className="text-center py-10 text-gray-500 font-typewriter">
                <Gift className="w-12 h-12 mx-auto text-amber-900/50 mb-3" />
                No reward claims yet.
              </div>
            ) : (
              <div className="rounded-md border border-amber-900/30 overflow-hidden">
                <Table>
                  <TableHeader className="bg-amber-900/20">
                    <TableRow className="border-amber-900/30">
                      <TableHead className="font-headline text-amber-500">Member</TableHead>
                      <TableHead className="font-headline text-amber-500">Reward</TableHead>
                      <TableHead className="font-headline text-amber-500">Claimed</TableHead>
                      <TableHead className="font-headline text-amber-500">Status</TableHead>
                      <TableHead className="text-right font-headline text-amber-500">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {claims.map(c => (
                      <TableRow key={c.id} className="border-amber-900/30 hover:bg-amber-900/10" data-testid={`claim-row-${c.id}`}>
                        <TableCell>
                          <p className="font-medium text-sm text-gray-200">{c.userDisplayName}</p>
                          <p className="text-xs text-gray-500 font-typewriter">{c.userEmail}</p>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm text-amber-300 font-typewriter">{c.rewardTitle}</p>
                          <p className="text-xs text-gray-500 font-typewriter">{c.rankName} · {c.milestoneMonths} mo{c.tierName ? ` · ${c.tierName}` : ''}</p>
                          {c.selectedOption && <p className="text-xs text-amber-400 font-typewriter mt-0.5">↳ Chose: {c.selectedOption}</p>}
                          {c.note && <p className="text-xs text-gray-400 italic mt-0.5">“{c.note}”</p>}
                        </TableCell>
                        <TableCell className="text-xs text-gray-400 font-typewriter">
                          {c.claimedAt ? new Date(c.claimedAt).toLocaleDateString() : '—'}
                        </TableCell>
                        <TableCell>
                          {c.status === 'fulfilled' ? (
                            <Badge className="bg-green-800 text-white font-headline tracking-wider"><CheckCircle2 className="w-3 h-3 mr-1" />FULFILLED</Badge>
                          ) : (
                            <Badge className="bg-amber-700 text-black font-headline tracking-wider"><Clock className="w-3 h-3 mr-1" />PENDING</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {c.status !== 'fulfilled' && (
                            <Button size="sm" onClick={() => handleFulfill(c)} className="bg-green-700 hover:bg-green-800 text-white font-headline tracking-wider" data-testid={`fulfill-claim-${c.id}`}>
                              <CheckCircle2 className="w-4 h-4 mr-1" /> Fulfill
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>

          {/* Ladder editor */}
          <TabsContent value="ladder">
            <div className="flex justify-end mb-3">
              <Button onClick={openCreate} className="bg-amber-600 hover:bg-amber-700 text-black font-headline tracking-wider" data-testid="add-milestone-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Milestone
              </Button>
            </div>
            <div className="space-y-2">
              {milestones.map(m => (
                <div key={m.id} className={`flex items-center gap-4 p-3 rounded-lg border ${m.enabled === false ? 'border-gray-800 bg-gray-950/40 opacity-60' : 'border-amber-900/40 bg-gray-900/60'}`} data-testid={`admin-milestone-${m.months}`}>
                  <RankBadge rank={m} size="md" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-headline text-amber-200 tracking-wider">{m.rankName}</span>
                      <span className="text-xs text-gray-500 font-typewriter">{m.months} mo</span>
                      {m.isOG && <Badge className="bg-yellow-700/40 text-yellow-300 text-[9px] font-headline tracking-widest">OG</Badge>}
                      {m.enabled === false && <Badge variant="outline" className="text-[9px] border-gray-600 text-gray-500">HIDDEN</Badge>}
                    </div>
                    {Object.keys(m.tierRewards || {}).length > 0 ? (
                      <div className="flex flex-wrap gap-1.5 mt-1">
                        {paidTiers.map(t => {
                          const r = (m.tierRewards || {})[String(t.level)];
                          return r?.title ? (
                            <span key={t.id} className="text-[10px] font-typewriter px-1.5 py-0.5 rounded bg-amber-900/20 text-amber-300 border border-amber-900/40">
                              {t.name}: {r.title}
                            </span>
                          ) : null;
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 italic font-typewriter">No rewards</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(m)} className="text-amber-500 hover:bg-amber-900/20" data-testid={`edit-milestone-${m.months}`}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(m)} className="text-red-500 hover:bg-red-900/20" data-testid={`delete-milestone-${m.months}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>

      {/* Milestone edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-gray-900 border-2 border-amber-900/30 sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-headline text-xl text-amber-500 tracking-wider">
              {editing ? 'EDIT MILESTONE' : 'NEW MILESTONE'}
            </DialogTitle>
            <DialogDescription className="font-typewriter text-gray-400">
              Ranks unlock automatically by paid tenure. Rewards are claimable by members.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-amber-500 font-headline tracking-wider text-xs">TENURE (MONTHS)</Label>
                <Input type="number" min="0" value={form.months} onChange={(e) => setForm({ ...form, months: parseInt(e.target.value) || 0 })} className="bg-gray-800 border-amber-900/50 font-typewriter" data-testid="milestone-months-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-amber-500 font-headline tracking-wider text-xs">RANK NAME</Label>
                <Input value={form.rankName} onChange={(e) => setForm({ ...form, rankName: e.target.value })} className="bg-gray-800 border-amber-900/50 font-typewriter" data-testid="milestone-name-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-amber-500 font-headline tracking-wider text-xs">ICON</Label>
                <Select value={form.icon} onValueChange={(v) => setForm({ ...form, icon: v })}>
                  <SelectTrigger className="bg-gray-800 border-amber-900/50 font-typewriter text-gray-200" data-testid="milestone-icon-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-amber-900/50 text-gray-200">
                    {ICON_OPTIONS.map(i => <SelectItem key={i} value={i} className="font-typewriter">{i}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-amber-500 font-headline tracking-wider text-xs">COLOR</Label>
                <Select value={form.color} onValueChange={(v) => setForm({ ...form, color: v })}>
                  <SelectTrigger className="bg-gray-800 border-amber-900/50 font-typewriter text-gray-200" data-testid="milestone-color-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-amber-900/50 text-gray-200">
                    {COLOR_OPTIONS.map(c => <SelectItem key={c} value={c} className="font-typewriter">{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-amber-500 font-headline tracking-wider text-xs">RANK FLAVOR TEXT (shown under the rank)</Label>
              <Textarea value={form.rewardDescription} onChange={(e) => setForm({ ...form, rewardDescription: e.target.value })} rows={2} className="bg-gray-800 border-amber-900/50 font-typewriter" data-testid="milestone-flavor-input" placeholder="e.g. Three months running with the crew. You're muscle now." />
            </div>

            {/* Per-tier rewards */}
            <div className="space-y-3 border-t border-amber-900/30 pt-3">
              <p className="text-amber-500 font-headline tracking-wider text-xs">REWARDS BY TIER <span className="text-gray-500 normal-case font-typewriter">(leave title blank = no reward for that tier)</span></p>
              {paidTiers.map(t => {
                const r = form.tierRewards[String(t.level)] || {};
                return (
                  <div key={t.id} className="rounded-lg border border-amber-900/30 bg-gray-950/50 p-3 space-y-2" data-testid={`tier-reward-editor-${t.level}`}>
                    <p className="font-headline text-amber-300 tracking-wider text-sm">{t.name}</p>
                    <Input
                      value={r.title || ''}
                      onChange={(e) => setTierReward(String(t.level), 'title', e.target.value)}
                      placeholder="Reward title (blank = none)"
                      className="bg-gray-800 border-amber-900/50 font-typewriter text-sm"
                      data-testid={`tier-reward-title-${t.level}`}
                    />
                    <Input
                      value={r.description || ''}
                      onChange={(e) => setTierReward(String(t.level), 'description', e.target.value)}
                      placeholder="Reward description"
                      className="bg-gray-800 border-amber-900/50 font-typewriter text-sm"
                      data-testid={`tier-reward-desc-${t.level}`}
                    />
                    <Textarea
                      value={r.optionsText || ''}
                      onChange={(e) => setTierReward(String(t.level), 'optionsText', e.target.value)}
                      rows={2}
                      placeholder="Optional choices — one per line (member picks one when claiming)"
                      className="bg-gray-800 border-amber-900/50 font-typewriter text-xs"
                      data-testid={`tier-reward-options-${t.level}`}
                    />
                  </div>
                );
              })}
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Switch checked={form.isOG} onCheckedChange={(v) => setForm({ ...form, isOG: v })} data-testid="milestone-og-switch" />
                <Label className="text-gray-300 font-typewriter text-sm">OG Badge (permanent recognition)</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} data-testid="milestone-enabled-switch" />
                <Label className="text-gray-300 font-typewriter text-sm">Enabled</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-gray-600 text-gray-400 hover:bg-gray-800 font-headline tracking-wider">
              <X className="w-4 h-4 mr-2" /> Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-amber-600 hover:bg-amber-700 text-black font-headline tracking-wider" data-testid="save-milestone-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default GamificationAdminPanel;
